import React, { useState, useEffect, useRef } from 'react';
import Dashboard from '@/components/Dashboard';
import Workspace from '@/components/Workspace';
import { loadFromUrl } from '@/utils/shareUtils';
import { getAllWorkspaces, createWorkspace, deleteWorkspace, saveWorkspace } from '@/utils/storageUtils';
import { loadFromAirtable, saveToAirtable, isAirtableConfigured, fetchAllWorkspaces, deleteFromAirtable } from '@/services/airtable';
import '@/styles/tree.css';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

function App() {
  const [view, setView] = useState('loading');
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [sharedData, setSharedData] = useState(null);
  const [cloudStatus, setCloudStatus] = useState('idle');
  const [isSyncingDashboard, setIsSyncingDashboard] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const init = async () => {
      // 1. Check for Short ID (Airtable) - Bypass Auth for Shared Links
      const params = new URLSearchParams(window.location.search);
      const shortId = params.get('id');
      if (shortId) {
        const airtableData = await loadFromAirtable(shortId);
        if (airtableData) {
          setSharedData(airtableData);
          setView('workspace');
          return;
        } else {
          setView('error');
          return;
        }
      }

      // 2. Check for Legacy Shared Link - Bypass Auth
      const shared = loadFromUrl();
      if (shared) {
        setSharedData(shared);
        setView('workspace');
        return;
      }

      // 3. Check Authentication for Dashboard
      checkAuthCookie();
      
      // 4. Load Workspaces
      await loadDashboardData();
      setView('dashboard');
    };
    init();
  }, []);

  const checkAuthCookie = () => {
    const match = document.cookie.split('; ').find(row => row.startsWith('sc_auth='));
    if (match) {
      setIsAuthenticated(true);
    }
  };

  const handleLogin = () => {
    // Set cookie for 30 days
    document.cookie = "sc_auth=true; max-age=2592000; path=/";
    setIsAuthenticated(true);
  };

  const loadDashboardData = async () => {
    setIsSyncingDashboard(true);
    // Get Local
    let localWorkspaces = getAllWorkspaces();
    let mergedWorkspaces = [...localWorkspaces];

    // Get Cloud (if connected)
    if (isAirtableConfigured()) {
      try {
        const cloudWorkspaces = await fetchAllWorkspaces();
        
        // Merge Logic
        const localMap = new Map(mergedWorkspaces.map(w => [w.id, w]));
        const newItems = [];
        
        cloudWorkspaces.forEach(cloudWs => {
          if (localMap.has(cloudWs.id)) {
            const localWs = localMap.get(cloudWs.id);
            // Sync cloud ID and status if missing locally
            localWs._airtableId = cloudWs._airtableId;
            if (cloudWs.status && !localWs.status) localWs.status = cloudWs.status;
          } else {
            newItems.push(cloudWs);
          }
        });

        mergedWorkspaces = [...mergedWorkspaces, ...newItems];
      } catch (e) {
        console.error("Failed to sync dashboard with Airtable", e);
      }
    }
    setWorkspaces(mergedWorkspaces);
    setIsSyncingDashboard(false);
  };

  const handleOpenWorkspace = (id) => {
    setActiveWorkspaceId(id);
    setView('workspace');
    window.history.pushState({}, document.title, window.location.pathname);
  };

  const handleCreateWorkspace = async () => {
    // Just create locally initially to avoid duplicates
    // We only save to cloud when user explicitly hits Save
    const newWs = createWorkspace();
    // Default status is 'published'
    newWs.status = 'published';
    
    // Update state immediately
    setWorkspaces(prev => [newWs, ...prev]);
    setActiveWorkspaceId(newWs.id);
    setView('workspace');
    window.history.pushState({}, document.title, window.location.pathname);
  };

  // SOFT DELETE: Move to Trash
  const handleSoftDeleteWorkspace = async (id) => {
    const workspaceToTrash = workspaces.find(w => w.id === id);
    if (!workspaceToTrash) return;

    const updatedData = { ...workspaceToTrash, status: 'trash' };
    
    // 1. Update Local
    saveWorkspace(id, updatedData);
    
    // 2. Update Cloud
    if (isAirtableConfigured()) {
      try {
        await saveToAirtable(id, updatedData.title, updatedData, updatedData._airtableId);
      } catch (e) {
        console.error("Failed to sync soft delete", e);
      }
    }

    // 3. Update State
    setWorkspaces(prev => prev.map(w => w.id === id ? updatedData : w));
  };

  // RESTORE: Move to Published
  const handleRestoreWorkspace = async (id) => {
    const workspaceToRestore = workspaces.find(w => w.id === id);
    if (!workspaceToRestore) return;

    const updatedData = { ...workspaceToRestore, status: 'published' };
    
    // 1. Update Local
    saveWorkspace(id, updatedData);
    
    // 2. Update Cloud
    if (isAirtableConfigured()) {
      try {
        await saveToAirtable(id, updatedData.title, updatedData, updatedData._airtableId);
      } catch (e) {
        console.error("Failed to sync restore", e);
      }
    }

    // 3. Update State
    setWorkspaces(prev => prev.map(w => w.id === id ? updatedData : w));
  };

  // HARD DELETE: Remove permanently
  const handlePermanentDeleteWorkspace = async (id) => {
    const workspaceToDelete = workspaces.find(w => w.id === id);
    
    // 1. Delete Local
    const updated = deleteWorkspace(id);
    setWorkspaces(updated);

    // 2. Delete Cloud
    if (isAirtableConfigured() && workspaceToDelete?._airtableId) {
      await deleteFromAirtable(workspaceToDelete._airtableId);
    }
  };

  // ONLY called when the user clicks SAVE in the workspace
  const handleManualSave = async (id, data) => {
    // Ensure status is preserved or defaulted
    const currentData = workspaces.find(w => w.id === id);
    const status = currentData?.status || 'published';
    const fullData = { ...data, status };

    // 1. Save Locally
    saveWorkspace(id, fullData);

    // 2. Save to Cloud (if connected)
    if (isAirtableConfigured()) {
      setCloudStatus('saving');
      try {
        // Find current workspace to get its _airtableId if we have it
        const currentWs = workspaces.find(w => w.id === id);
        const airtableId = currentWs?._airtableId;
        
        const newRecordId = await saveToAirtable(id, data.title, fullData, airtableId);

        // Update our state with the new/confirmed ID
        if (newRecordId) {
          setWorkspaces(prev => prev.map(w => {
            if (w.id === id) return { ...w, ...fullData, _airtableId: newRecordId };
            return w;
          }));
        } else {
          // Update local state content anyway
          setWorkspaces(prev => prev.map(w => w.id === id ? { ...w, ...fullData } : w));
        }
        setCloudStatus('saved');
        setTimeout(() => setCloudStatus('idle'), 2000);
      } catch (e) {
        console.error("Cloud Save Error:", e);
        setCloudStatus('error');
      }
    } else {
      // Just update local state if no cloud
      setWorkspaces(prev => prev.map(w => w.id === id ? { ...w, ...fullData } : w));
      setCloudStatus('saved');
      setTimeout(() => setCloudStatus('idle'), 1000);
    }
  };

  const handleBackToDashboard = async () => {
    setActiveWorkspaceId(null);
    setSharedData(null);
    setCloudStatus('idle');
    setView('dashboard');
    window.history.pushState({}, document.title, window.location.pathname);
    await loadDashboardData();
  };

  if (view === 'loading') return <div className="h-screen w-screen bg-slate-50 flex items-center justify-center text-slate-400">Loading...</div>;

  if (view === 'error') {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 bg-slate-50">
        <h2 className="text-xl font-bold text-slate-800">Project Not Found</h2>
        <p className="text-slate-500">The shared link might be invalid or expired.</p>
        <button onClick={() => window.location.href = window.location.pathname} className="text-indigo-600 hover:underline font-medium">
          Go to Home
        </button>
      </div>
    );
  }

  if (view === 'workspace') {
    const workspaceData = sharedData || workspaces.find(w => w.id === activeWorkspaceId);
    
    if (!workspaceData) {
      return (
        <div className="h-screen flex flex-col items-center justify-center gap-4">
          <h2 className="text-xl font-bold text-slate-800">Workspace Not Found</h2>
          <button onClick={handleBackToDashboard} className="text-indigo-600 hover:underline">Return to Dashboard</button>
        </div>
      );
    }

    return (
      <Workspace 
        workspaceData={workspaceData} 
        isReadOnly={!!sharedData}
        // Removed explicit onUpdate prop to disable auto-save
        onCloudSave={!sharedData ? handleManualSave : undefined}
        onBack={!sharedData ? handleBackToDashboard : undefined}
        cloudStatus={cloudStatus}
      />
    );
  }

  return (
    <>
      <Dashboard 
        workspaces={workspaces} 
        onOpen={handleOpenWorkspace} 
        onCreate={handleCreateWorkspace} 
        onDelete={handleSoftDeleteWorkspace}
        onRestore={handleRestoreWorkspace}
        onPermanentDelete={handlePermanentDeleteWorkspace}
        isAuthenticated={isAuthenticated}
        onLogin={handleLogin}
      />
      {isSyncingDashboard && (
        <div className="fixed bottom-4 right-4 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-lg border border-slate-100 flex items-center gap-2 text-xs font-medium text-slate-500 pointer-events-none">
          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-indigo-500" /> Syncing with Airtable...
        </div>
      )}
    </>
  );
}

export default App;